import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CreateIntegrationJournalCommand } from '@assetaccountant/integrations/models/common/create-integration-journal-command';

import { RegisterIntegration } from '@assetaccountant/core/models/register-integration';
import { environment } from '../../../../environments/environment';
import { CreateIntegrationJournalResponse } from '../models/common/create-integration-journal-response';
import { DisconnectIntegrationsResponse } from '../models/common/disconnect-integrations-command';
import { GetIntegrationResponse } from '../models/common/get-integration-response';
import { ListAccountsResponse } from '../models/common/list-accounts-response';
import { ListDraftAssetsResponse } from '../models/common/list-draft-assets-response';
import { ListIntegrationClassificationsResponse } from '../models/common/list-integration-classifications-response';
import { ListIntegrationsResponse } from '../models/common/list-integrations-response';
import { IntegrationType } from '../models/integration-type';
import { MicrosoftDynamicsService } from './dynamics.service';
import { QuickBooksService } from './quickbooks.service';
import { SageService } from './sage.service';
import { SageV2Service } from './sagev2.service';
import { XeroService } from './xero.service';

@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private xeroService: XeroService,
    private quickBooksService: QuickBooksService,
    private dynamicsService: MicrosoftDynamicsService,
    private sageService: SageService,
    private sageV2Service: SageV2Service,
  ) {}

  listIntegrations(integrationType: IntegrationType, tenantId: string) {
    return this.http.get<ListIntegrationsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations?integrationType=${integrationType}&tenantId=${tenantId}`,
    );
  }

  listClassifications(integrationId: string) {
    return this.http.get<ListIntegrationClassificationsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/${integrationId}/classifications`,
    );
  }

  listDraftAssets(integrationId: string, page = 1) {
    return this.http.get<ListDraftAssetsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/${integrationId}/draftAssets?page=${page}`,
    );
  }

  listAccounts(integrationId: string) {
    return this.http.get<ListAccountsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/${integrationId}/accounts`,
    );
  }

  createJournal(integrationId: string, command: CreateIntegrationJournalCommand) {
    return this.http.post<CreateIntegrationJournalResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/${integrationId}/journals`,
      command,
    );
  }

  disconnectIntegration(integrationId: string) {
    return this.http.delete(`${environment.servicesApiUrl}/api/v2/integrations/${integrationId}`);
  }

  disconnectIntegrationTenant(integrationType: IntegrationType, tenantId: string) {
    return this.http.delete<DisconnectIntegrationsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations?tenantId=${tenantId}&integrationType=${integrationType}`,
    );
  }

  getIntegration(integrationType: IntegrationType) {
    return this.http.get<GetIntegrationResponse>(
      `${environment.servicesApiUrl}/api/integrations/${integrationType}`,
    );
  }

  handleError(error, integration?: RegisterIntegration) {
    // Sometimes the integration details aren't available (e.g. no integration exists)
    if (!integration) {
      throw error;
    }
    integration.metadata.TenantId = integration.tenantId;
    switch (integration.integrationType) {
      case IntegrationType.QuickBooks:
        this.quickBooksService.handleError(integration.registerId, error, this.router.url);
        break;
      case IntegrationType.Xero:
        this.xeroService.handleError(
          integration.registerId,
          error,
          this.router.url,
          integration.metadata,
        );
        break;
      case IntegrationType.MicrosoftDynamics:
        this.dynamicsService.handleError(
          integration.registerId,
          error,
          this.router.url,
          integration.metadata,
        );
        break;
      case IntegrationType.Sage:
        if (integration.isLegacy) {
          this.sageService.handleError(integration.registerId, error, integration);
        } else {
          this.sageV2Service.handleError(integration.registerId, error, this.router.url);
        }
        break;
      default:
        throw error;
    }
  }
}
